// applyGroupValues — the SINGLE place that turns a `groups` array (each field
// carrying `.value`, each addRow group carrying `.rows`) into antd form state.
//
// This is the exact mechanism Edit-prefill has always used (getFormGroups with
// an `id` embeds field.value/group.rows server-side via AttachFieldValues).
// It's extracted here so any OTHER source of the same shape — notably an AI
// Action's response, which the gateway resolves through the identical
// AttachFieldValues engine — can be applied to the form with the same code,
// instead of maintaining a second, parallel field-mapping implementation.
import { buildInitialValue, buildRepeatFieldInitial, getDeep } from './payloadTransformer';

// truthy tolerates the boolean-ish shapes a config flag can arrive in.
const isTruthyFlag = (v) => v === true || v === 1 || v === '1';
import { buildFileUrl } from '../detail/renderConfig';

const empty = (v) => v === undefined || v === null || v === '';

export function namePathFromString(path) {
  return String(path ?? '').split('.').map((part) => part.trim()).filter(Boolean);
}

export function writeFormValue(target, path, value) {
  const parts = Array.isArray(path) ? path : namePathFromString(path);
  if (!parts.length) return target;
  let cursor = target;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i];
    if (!cursor[key] || typeof cursor[key] !== 'object' || Array.isArray(cursor[key])) cursor[key] = {};
    cursor = cursor[key];
  }
  cursor[parts[parts.length - 1]] = value;
  return target;
}

function firstConfiguredValue(source, keys = []) {
  if (!source || typeof source !== 'object') return undefined;
  for (const key of keys) {
    if (!key) continue;
    const val = getDeep(source, key);
    if (!empty(val)) return val;
  }
  return undefined;
}

function firstMatchingValue(source, pattern) {
  if (!source || typeof source !== 'object') return undefined;
  const entries = Object.entries(source);
  const direct = entries.find(([key, val]) => pattern.test(key) && !empty(val));
  if (direct) return direct[1];
  for (const [, val] of entries) {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const nested = firstMatchingValue(val, pattern);
      if (!empty(nested)) return nested;
    }
  }
  return undefined;
}

// fileListFromValue — turn a file field's embedded value (from getFormGroups,
// or an AI response resolved the same way) into the antd fileList the file
// control expects. The value may be a full document object ({ location,
// uploadName/name, uniqueName }), a bare original filename string, or an
// array of either. Returns undefined when empty.
export function fileListFromValue(stored, field) {
  const list = Array.isArray(stored) ? stored : (stored ? [stored] : []);
  if (!list.length) return undefined;
  const mapped = list.map((f, i) => {
    if (typeof f === 'string') {
      return { uid: `${field.field}-${i}`, name: f, status: 'done' };
    }
    if (!f || typeof f !== 'object') return null;
    const rawUrl = firstConfiguredValue(f, [field.fileUrlKey, field.locationKey])
      ?? firstMatchingValue(f, /(location|url|path)$/i)
      ?? '';
    const fileName = firstConfiguredValue(f, [field.fileNameKey])
      ?? firstMatchingValue(f, /(uploadName|uploadedFileName|fileName|name)$/i)
      ?? '';
    // A stored object with no usable url AND no real filename carries no actual
    // file (e.g. an all-null document sub-object from a parsed AI response) —
    // skip it rather than showing a bogus "file" chip with nothing behind it.
    if (!rawUrl && !fileName) return null;
    const fullUrl = rawUrl && !String(rawUrl).startsWith('http')
      ? buildFileUrl(rawUrl)
      : rawUrl;
    return {
      uid: String(f.id ?? f._id ?? f.uniqueName ?? f.documentCopyUniqueName ?? `${field.field}-${i}`),
      name: fileName || 'file',
      status: 'done',
      existing: true,
      url: fullUrl || undefined,
    };
  }).filter(Boolean);
  return mapped.length ? mapped : undefined;
}

// buildRowFileList — turn an addRow row's stored file metadata into the antd
// fileList. Returns undefined when the row has no file.
export function buildRowFileList(row, field) {
  if (!row || typeof row !== 'object') return undefined;
  const name = firstConfiguredValue(row, [field.fileNameKey, field.field])
    ?? firstMatchingValue(row, /(uploadName|uploadedFileName|fileName|name)$/i);
  const rawUrl = firstConfiguredValue(row, [field.fileUrlKey, field.locationKey])
    ?? firstMatchingValue(row, /(location|url|path)$/i)
    ?? '';
  if (!name && !rawUrl) return undefined;
  const fullUrl = rawUrl && !String(rawUrl).startsWith('http')
    ? buildFileUrl(rawUrl)
    : rawUrl;
  const uid = row.documentCopyUniqueName ?? row.uniqueName ?? name ?? `${field.field}-0`;
  return [{
    uid: String(uid),
    name: name ?? 'file',
    status: 'done',
    existing: true,
    url: fullUrl || undefined,
  }];
}

// buildAddRowInitial — turn a repeatable (addRow) group's backend-embedded
// stored rows (group.rows) into the Form.List initialValue shape: an array
// with ONE object per stored row, each mapped through buildInitialValue (file
// fields via the row's sibling keys). Returns [] when the group has no stored
// rows, padded to group.minRows.
export function buildAddRowInitial(group) {
  const storedRows = Array.isArray(group.rows) ? group.rows : null;
  const minRows = Math.max(0, Number(group.minRows ?? 0) || 0);
  const mapRow = (row, readFile, readScalar) => {
    const rowVals = {};
    (group.fields ?? []).forEach((field) => {
      if (!field.field) return;
      if (field.type === 'file') {
        const fileList = readFile(row, field);
        if (fileList) writeFormValue(rowVals, field.field, fileList);
        return;
      }
      const stored = readScalar(row, field);
      if (stored === undefined || stored === null) return;
      // A repeat field inside the row (nested Form.List) hydrates as an array,
      // each element normalised for its control; a plain field as a single value.
      writeFormValue(rowVals, field.field, isTruthyFlag(field.addRow)
        ? buildRepeatFieldInitial(field, stored)
        : buildInitialValue(field, stored));
    });
    return rowVals;
  };

  if (storedRows && storedRows.length > 0) {
    const rows = storedRows.map((row) => mapRow(
      row,
      (r, field) => buildRowFileList(r, field),
      (r, field) => getDeep(r, field.field),
    ));
    while (rows.length < minRows) rows.push({});
    return rows;
  }

  // Fallback for an older backend that embeds only per-field field.value (row 0).
  const rowVals = mapRow(
    group,
    (_g, field) => fileListFromValue(field.value, field),
    (_g, field) => field.value,
  );
  const rows = Object.keys(rowVals).length > 0 ? [rowVals] : [];
  while (rows.length < minRows) rows.push({});
  return rows;
}

// applyScalarFieldValues — walk every NON-addRow group's fields, read each
// field's embedded `.value`, shape it (buildInitialValue / fileListFromValue /
// checkbox-transformRule reversal), and set it on the form in one batch.
// AddRow groups are skipped — their rows only apply correctly as a Form.List
// `initialValue` at mount (or via an explicit remount for a post-mount
// update), never via setFieldsValue.
export function applyScalarFieldValues(form, groups = []) {
  const values = {};

  groups.forEach((group) => {
    if (group.toggleEnabled && group.toggleField) {
      writeFormValue(values, group.toggleField, Boolean(group.toggleValue));
    }
    if (group.addRow) return;

    (group.fields ?? []).forEach((field) => {
      if (!field.field) return;
      // A repeatable (addRow) field prefills via its own Form.List initialValue
      // at mount (buildRepeatFieldInitial) — setFieldsValue on a Form.List that
      // mounted empty only keeps the last item, so skip it here.
      if (field.addRow) return;
      const stored = field.value;
      const name = field.field;

      if (field.type === 'file') {
        const fileList = fileListFromValue(stored, field);
        if (fileList) writeFormValue(values, name, fileList);
        return;
      }

      if (stored === undefined || stored === null) return;

      if (field.type === 'checkbox' && Array.isArray(field.options) && field.options.length > 0) {
        const map = field.transformRule?.map;
        if (map) {
          const match = Object.entries(map).find(([, to]) => String(to) === String(stored));
          writeFormValue(values, name, match ? [match[0]] : []);
        } else {
          writeFormValue(values, name, Array.isArray(stored) ? stored : [stored]);
        }
        return;
      }

      writeFormValue(values, name, buildInitialValue(field, stored));
    });
  });

  if (Object.keys(values).length > 0) form.setFieldsValue(values);
  return values;
}
